import React, { useContext, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Input,
  Button,
  FormGroup,
  Label,
} from "reactstrap";

import { ApiContext } from "../../contexts/ApiContext";
import { useNotification } from "../../contexts/NotificationContext";
import TableService from "../../service/table.service";
import { getCardapioUrl } from "../../utils/cardapioUrl";

const TablesPage = () => {
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (account?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await TableService.list(account.id);
      setTables(data || []);
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao carregar mesas", "icon-alert-circle-exc");
    } finally {
      setLoading(false);
    }
  };

  const buildLink = (table) => {
    const base = getCardapioUrl(account?.menuCode);
    if (!base) return "";
    return `${base}?mesa=${encodeURIComponent(table.code)}`;
  };

  const handleCreate = async () => {
    if (!name.trim() || !code.trim()) {
      notify("tr", "warning", "Informe nome e código da mesa", "icon-alert-circle-exc");
      return;
    }
    setCreating(true);
    try {
      await TableService.create(account.id, { name: name.trim(), code: code.trim() });
      setName("");
      setCode("");
      notify("tr", "success", "Mesa criada!", "icon-check-2");
      load();
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao criar mesa (código já existe?)", "icon-alert-circle-exc");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (table) => {
    try {
      await TableService.update(table.id, { active: !table.active });
      setTables((prev) => prev.map((t) => (t.id === table.id ? { ...t, active: !t.active } : t)));
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao atualizar mesa", "icon-alert-circle-exc");
    }
  };

  const handleDelete = async (table) => {
    try {
      await TableService.remove(table.id);
      setTables((prev) => prev.filter((t) => t.id !== table.id));
      notify("tr", "success", "Mesa removida", "icon-check-2");
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao remover mesa", "icon-alert-circle-exc");
    }
  };

  const copyLink = (table) => {
    try {
      navigator.clipboard.writeText(buildLink(table));
      notify("tr", "success", "Link copiado!", "icon-check-2");
    } catch (e) {
      // ignore
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Mesas</CardTitle>
              <p className="card-category">
                Gerencie as mesas e gere o QR Code para pedidos no local.
              </p>
            </CardHeader>
            <CardBody>
              <Row className="align-items-end">
                <Col md="5">
                  <FormGroup>
                    <Label>Nome da mesa</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ex: Mesa 1"
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Código (usado no QR)</Label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Ex: 1"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Button color="primary" block onClick={handleCreate} disabled={creating}>
                      {creating ? "Adicionando..." : "Adicionar mesa"}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {loading && (
          <Col md="12">
            <div>Carregando mesas...</div>
          </Col>
        )}
        {!loading && tables.length === 0 && (
          <Col md="12">
            <p className="text-muted">Nenhuma mesa cadastrada.</p>
          </Col>
        )}
        {tables.map((table) => (
          <Col md="4" key={table.id}>
            <Card>
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  {table.name}{" "}
                  {!table.active && <small className="text-muted">(inativa)</small>}
                </CardTitle>
              </CardHeader>
              <CardBody className="text-center">
                <div style={{ background: "#fff", padding: 12, display: "inline-block", borderRadius: 8 }}>
                  <QRCodeCanvas value={buildLink(table)} size={160} />
                </div>
                <div style={{ marginTop: 12, wordBreak: "break-all", fontSize: 12 }}>
                  {buildLink(table)}
                </div>
                <div className="mt-3 d-flex justify-content-center" style={{ gap: 8 }}>
                  <Button size="sm" color="info" onClick={() => copyLink(table)}>
                    Copiar link
                  </Button>
                  <Button size="sm" color="secondary" onClick={() => handleToggleActive(table)}>
                    {table.active ? "Desativar" : "Ativar"}
                  </Button>
                  <Button size="sm" color="danger" onClick={() => handleDelete(table)}>
                    Excluir
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default TablesPage;
